const VIEWPORT_IDS = ["desktop", "wide", "laptop", "tablet", "mobile"];

const DESKTOP_DEFAULTS = Object.freeze({
  columns: 0,
  minCardWidth: 220,
  cardMinHeight: 0,
  imageHeight: 160,
  gap: 16,
  bodyPadding: 12,
  borderWidth: 1,
  borderRadius: 0,
  borderColor: "#d1d5db",
  backgroundColor: "#ffffff",
  titleColor: "#1f2937",
  priceColor: "#0092e0",
  metaColor: "#64748b",
  statusColor: "#475569",
});

const VIEWPORT_DEFAULTS = Object.freeze({
  desktop: Object.freeze({}),
  wide: Object.freeze({}),
  laptop: Object.freeze({}),
  tablet: Object.freeze({
    columns: 0,
    minCardWidth: 148,
    imageHeight: 128,
    gap: 10,
  }),
  mobile: Object.freeze({
    columns: 1,
    minCardWidth: 148,
    imageHeight: 128,
    gap: 10,
  }),
});

const clampNumber = (value, fallback, min, max) => {
  const number = Number(value);
  if (!Number.isFinite(number)) return fallback;
  return Math.min(max, Math.max(min, Math.round(number)));
};

const normalizeColor = (value, fallback) => {
  const color = typeof value === "string" ? value.trim() : "";
  if (/^#[0-9a-f]{6}$/i.test(color)) return color;
  if (/^#[0-9a-f]{3}$/i.test(color)) {
    return `#${color
      .slice(1)
      .split("")
      .map((character) => `${character}${character}`)
      .join("")}`;
  }
  return fallback;
};

const normalizeFields = (value = {}, fallback = DESKTOP_DEFAULTS) => ({
  columns: clampNumber(value.columns, fallback.columns, 0, 6),
  minCardWidth: clampNumber(value.minCardWidth, fallback.minCardWidth, 120, 800),
  cardMinHeight: clampNumber(value.cardMinHeight, fallback.cardMinHeight, 0, 1200),
  imageHeight: clampNumber(value.imageHeight, fallback.imageHeight, 80, 600),
  gap: clampNumber(value.gap, fallback.gap, 0, 80),
  bodyPadding: clampNumber(value.bodyPadding, fallback.bodyPadding, 0, 80),
  borderWidth: clampNumber(value.borderWidth, fallback.borderWidth, 0, 12),
  borderRadius: clampNumber(value.borderRadius, fallback.borderRadius, 0, 80),
  borderColor: normalizeColor(value.borderColor, fallback.borderColor),
  backgroundColor: normalizeColor(value.backgroundColor, fallback.backgroundColor),
  titleColor: normalizeColor(value.titleColor, fallback.titleColor),
  priceColor: normalizeColor(value.priceColor, fallback.priceColor),
  metaColor: normalizeColor(value.metaColor, fallback.metaColor),
  statusColor: normalizeColor(value.statusColor, fallback.statusColor),
});

export const createDefaultFeaturedVehicleCardStyle = () => ({
  ...DESKTOP_DEFAULTS,
  viewportLayouts: {
    wide: { ...VIEWPORT_DEFAULTS.wide },
    laptop: { ...VIEWPORT_DEFAULTS.laptop },
    tablet: { ...VIEWPORT_DEFAULTS.tablet },
    mobile: { ...VIEWPORT_DEFAULTS.mobile },
  },
});

export const normalizeFeaturedVehicleCardStyle = (style = {}) => {
  const source = style && typeof style === "object" && !Array.isArray(style) ? style : {};
  const desktop = normalizeFields(source, DESKTOP_DEFAULTS);
  const sourceLayouts =
    source.viewportLayouts &&
    typeof source.viewportLayouts === "object" &&
    !Array.isArray(source.viewportLayouts)
      ? source.viewportLayouts
      : {};

  return {
    ...desktop,
    viewportLayouts: VIEWPORT_IDS.slice(1).reduce((layouts, viewportId) => {
      const defaultOverride = VIEWPORT_DEFAULTS[viewportId] || {};
      const fallback = normalizeFields({ ...desktop, ...defaultOverride }, desktop);
      const sourceOverride =
        sourceLayouts[viewportId] &&
        typeof sourceLayouts[viewportId] === "object" &&
        !Array.isArray(sourceLayouts[viewportId])
          ? sourceLayouts[viewportId]
          : {};
      const resolved = normalizeFields({ ...fallback, ...sourceOverride }, fallback);
      layouts[viewportId] = Object.keys(DESKTOP_DEFAULTS).reduce((override, key) => {
        if (resolved[key] !== desktop[key] || key in defaultOverride || key in sourceOverride) {
          override[key] = resolved[key];
        }
        return override;
      }, {});
      return layouts;
    }, {}),
  };
};

export const resolveFeaturedVehicleCardStyle = (style, viewportId = "desktop") => {
  const normalized = normalizeFeaturedVehicleCardStyle(style);
  if (viewportId === "desktop") {
    const { viewportLayouts: _viewportLayouts, ...desktop } = normalized;
    return desktop;
  }
  return normalizeFields(
    {
      ...normalized,
      ...(normalized.viewportLayouts?.[viewportId] || {}),
    },
    normalized
  );
};

export const patchFeaturedVehicleCardStyle = (
  style,
  viewportId = "desktop",
  patch = {}
) => {
  const normalized = normalizeFeaturedVehicleCardStyle(style);
  if (viewportId === "desktop") {
    return normalizeFeaturedVehicleCardStyle({ ...normalized, ...patch });
  }
  return normalizeFeaturedVehicleCardStyle({
    ...normalized,
    viewportLayouts: {
      ...(normalized.viewportLayouts || {}),
      [viewportId]: {
        ...(normalized.viewportLayouts?.[viewportId] || {}),
        ...patch,
      },
    },
  });
};

const gridTemplate = ({ columns, minCardWidth }) =>
  columns > 0
    ? `repeat(${columns}, minmax(0, 1fr))`
    : `repeat(auto-fill, minmax(min(100%, ${minCardWidth}px), 1fr))`;

export const buildFeaturedVehicleCardCssVariables = (style) => {
  const normalized = normalizeFeaturedVehicleCardStyle(style);
  return VIEWPORT_IDS.reduce((variables, viewportId) => {
    const resolved = resolveFeaturedVehicleCardStyle(normalized, viewportId);
    const suffix = viewportId;
    variables[`--wm-featured-grid-template-${suffix}`] = gridTemplate(resolved);
    variables[`--wm-featured-card-gap-${suffix}`] = `${resolved.gap}px`;
    variables[`--wm-featured-card-min-height-${suffix}`] =
      resolved.cardMinHeight > 0 ? `${resolved.cardMinHeight}px` : "0px";
    variables[`--wm-featured-card-image-height-${suffix}`] = `${resolved.imageHeight}px`;
    variables[`--wm-featured-card-padding-${suffix}`] = `${resolved.bodyPadding}px`;
    variables[`--wm-featured-card-border-width-${suffix}`] = `${resolved.borderWidth}px`;
    variables[`--wm-featured-card-border-radius-${suffix}`] = `${resolved.borderRadius}px`;
    variables[`--wm-featured-card-border-color-${suffix}`] = resolved.borderColor;
    variables[`--wm-featured-card-background-${suffix}`] = resolved.backgroundColor;
    variables[`--wm-featured-card-title-color-${suffix}`] = resolved.titleColor;
    variables[`--wm-featured-card-price-color-${suffix}`] = resolved.priceColor;
    variables[`--wm-featured-card-meta-color-${suffix}`] = resolved.metaColor;
    variables[`--wm-featured-card-status-color-${suffix}`] = resolved.statusColor;
    return variables;
  }, {});
};

export const FEATURED_VEHICLE_CARD_VIEWPORTS = VIEWPORT_IDS;
